import { useNavigate } from "@tanstack/react-router";
import { Bell, LogOut, User } from "lucide-react";

import { useCurrentUser, useRoles } from "@/auth/queries";
import { useSession } from "@/auth/session";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { LIVE } from "@/lib/live";

/**
 * The sidebar's answer to "who am I signed in as" (#270 §2) — until now the
 * shell never said.
 *
 * The role line has two sources, chosen by `LIVE` like everything else that
 * differs between a build with an API and one without:
 *
 * - **Against the API** it is the signed-in user's own roles, named by
 *   `/api/me` (`CurrentUser.roleNames`). It must not come from the roles list:
 *   that route needs `settings.roles.manage`, so for everyone else it answers
 *   403, and the persona it was matched against does not exist there (#429).
 * - **On fixtures** it is the active persona from `PersonaSwitcher`, not
 *   `currentUser`'s own fixed Administrator role: they are the same fact
 *   ("what can this session do") read two ways, and showing the fixed one
 *   here would contradict a sidebar that has just collapsed to four modules.
 */
export function AccountMenu() {
  const navigate = useNavigate();
  const { signOut } = useSession();
  const currentUserQuery = useCurrentUser();
  // In the phone sheet there is no room beside the sidebar: open upwards.
  const { isMobile } = useSidebar();

  function handleSignOut(): void {
    void signOut().then(() => navigate({ to: "/sign-in" }));
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton aria-label="Account" title="Account">
            <User aria-hidden />
            <span>{currentUserQuery.data?.name ?? "Account"}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side={isMobile ? "top" : "right"}
          align={isMobile ? "start" : "end"}
          className="w-56 max-w-[calc(100vw-2rem)]"
        >
          <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
            <span className="truncate text-sm font-medium">
              {currentUserQuery.data?.name ?? "Loading…"}
            </span>
            {LIVE ? <SignedInRoles /> : <PersonaRole />}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              void navigate({ to: "/account/notifications" });
            }}
          >
            <Bell aria-hidden />
            Notification settings
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleSignOut}>
            <LogOut aria-hidden />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

const ROLE_LINE_CLASS = "text-muted-foreground truncate text-xs";

/**
 * The roles the signed-in user holds, from the same `/api/me` answer the name
 * comes from — one request, and one every signed-in user is allowed to make.
 * A person holding no role has nothing to show, so the line is left out.
 */
function SignedInRoles() {
  const { data } = useCurrentUser();
  if (!data) return <span className={ROLE_LINE_CLASS}>…</span>;
  // The SPA and the API deploy separately, so for a while this page can be
  // talking to an API that does not send `roleNames` yet. No line, rather than
  // an error in the shell.
  const roleNames = data.roleNames as string[] | undefined;
  if (!roleNames || roleNames.length === 0) return null;

  const names = roleNames.join(", ");
  return (
    <span className={ROLE_LINE_CLASS} title={names}>
      {names}
    </span>
  );
}

/** The demo persona's role, found among the fixture roles. */
function PersonaRole() {
  const { activeRoleId } = useSession();
  const rolesQuery = useRoles();
  const roleName = rolesQuery.data?.items.find(
    (role) => role.id === activeRoleId,
  )?.name;

  return <span className={ROLE_LINE_CLASS}>{roleName ?? "…"}</span>;
}
